import { Injectable, signal } from '@angular/core';
import type { Toast, ToastOptions, ToastVariant } from './toast.types';

/** Longer for the tones a user has to read and act on. */
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 3500,
  info: 4000,
  warning: 7000,
  error: 7000,
};

const DEFAULT_TITLE: Record<ToastVariant, string> = {
  success: 'Success',
  info: 'Note',
  warning: 'Warning',
  error: 'Something went wrong',
};

/** Newest toasts push older ones down; past this the oldest is dropped. */
const MAX_VISIBLE = 4;

let nextId = 0;

/** App-wide toasts; `<ui-toast-container>` in the app shell renders the queue. */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly items = signal<readonly Toast[]>([]);
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  /** What the container renders. */
  readonly toasts = this.items.asReadonly();

  success(message: string, options?: ToastOptions): number {
    return this.show('success', message, options);
  }

  error(message: string, options?: ToastOptions): number {
    return this.show('error', message, options);
  }

  warning(message: string, options?: ToastOptions): number {
    return this.show('warning', message, options);
  }

  info(message: string, options?: ToastOptions): number {
    return this.show('info', message, options);
  }

  show(variant: ToastVariant, message: string, options?: ToastOptions): number {
    const body = message?.trim() ?? '';
    const title = options?.title?.trim() || DEFAULT_TITLE[variant];
    // A message identical to the title would just read twice.
    const text = body === title ? '' : body;
    const duration = options?.duration ?? DEFAULT_DURATION[variant];

    // The same alert again (e.g. a button clicked repeatedly): keep one, restart its countdown.
    const existing = this.items().find(
      (toast) => toast.variant === variant && toast.title === title && toast.message === text,
    );
    if (existing) {
      const restarted = { ...existing, duration, run: existing.run + 1 };
      this.items.update((current) => current.map((toast) => (toast.id === existing.id ? restarted : toast)));
      this.startTimer(restarted);
      return existing.id;
    }

    const toast: Toast = { id: nextId++, variant, title, message: text, duration, run: 0 };
    const next = [...this.items(), toast];
    // Ones pushed off the stack must not keep a timer running.
    next.slice(0, -MAX_VISIBLE).forEach((dropped) => this.stopTimer(dropped.id));
    this.items.set(next.slice(-MAX_VISIBLE));
    this.startTimer(toast);

    return toast.id;
  }

  dismiss(id: number): void {
    this.stopTimer(id);
    this.items.update((current) => current.filter((toast) => toast.id !== id));
  }

  /** Clears everything, e.g. on sign-out or a route that starts fresh. */
  clear(): void {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.items.set([]);
  }

  private startTimer(toast: Toast): void {
    this.stopTimer(toast.id);
    if (toast.duration > 0) {
      this.timers.set(toast.id, setTimeout(() => this.dismiss(toast.id), toast.duration));
    }
  }

  private stopTimer(id: number): void {
    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
  }
}
